/**
 * Leichtgewichtiges Toast-System ohne externe Abhängigkeit.
 *
 * Verwendung:
 *   import { toast } from '../lib/toast';
 *   toast('Gespeichert.');                          // neutral
 *   toast('Fehler beim Speichern.', 'error');
 *   toast(['Bedingung A fehlt', 'Bedingung B fehlt'], 'warning', 'Titel');
 *
 * <ToastHost dark={dark} /> einmal in App.tsx mounten.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getThemeColors } from './theme';

export type ToastType = 'info' | 'success' | 'warning' | 'error';

interface ToastItem {
  id: number;
  title?: string;
  lines: string[];
  type: ToastType;
}

type Listener = (t: ToastItem) => void;

let listener: Listener | null = null;
let nextId = 1;

export function toast(message: string | string[], type: ToastType = 'info', title?: string) {
  listener?.({
    id: nextId++,
    title,
    lines: Array.isArray(message) ? message : [message],
    type,
  });
}

const TYPE_COLORS: Record<ToastType, { border: string; accent: string }> = {
  info:    { border: '#2e90fa', accent: '#175cd3' },
  success: { border: '#12b76a', accent: '#027a48' },
  warning: { border: '#f79009', accent: '#b54708' },
  error:   { border: '#f04438', accent: '#b42318' },
};

// Fehler/Warnungen bleiben deutlich länger stehen, damit sie in Ruhe
// gelesen werden können; schließbar sind alle Toasts jederzeit über das ×.
const TOAST_DURATION_MS: Record<ToastType, number> = {
  info: 10000,
  success: 10000,
  warning: 15000,
  error: 15000,
};

export function ToastHost({ dark }: { dark: boolean }) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  useEffect(() => {
    listener = (item: ToastItem) => {
      setToasts(prev => [...prev, item]);
      window.setTimeout(() => {
        setToasts(prev => prev.filter(x => x.id !== item.id));
      }, TOAST_DURATION_MS[item.type]);
    };
    return () => { listener = null; };
  }, []);

  if (toasts.length === 0) return null;

  return (
    <div style={{
      position: 'fixed', top: 16, right: 16, zIndex: 9999,
      display: 'flex', flexDirection: 'column', gap: 10,
      maxWidth: 420, width: 'calc(100vw - 32px)',
    }}>
      {toasts.map(item => {
        const colors = TYPE_COLORS[item.type];
        return (
          <div
            key={item.id}
            className="fade-in"
            style={{
              background: t.card,
              border: `1px solid ${t.border}`,
              borderLeft: `4px solid ${colors.border}`,
              borderRadius: 10,
              padding: '12px 14px',
              boxShadow: dark ? '0 8px 24px rgba(0,0,0,0.45)' : '0 8px 24px -6px rgba(16,24,40,0.18)',
              fontFamily: 'Outfit, sans-serif',
              display: 'flex', gap: 10, alignItems: 'flex-start',
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              {item.title && (
                <div style={{ fontSize: 13, fontWeight: 700, color: colors.accent, marginBottom: item.lines.length ? 4 : 0 }}>
                  {item.title}
                </div>
              )}
              {item.lines.length === 1 && !item.title ? (
                <div style={{ fontSize: 13, color: t.text }}>{item.lines[0]}</div>
              ) : (
                <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12.5, color: t.text, lineHeight: 1.6 }}>
                  {item.lines.map((l, i) => <li key={i}>{l}</li>)}
                </ul>
              )}
            </div>
            <button
              onClick={() => setToasts(prev => prev.filter(x => x.id !== item.id))}
              aria-label={tr('common.action.close')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.muted, fontSize: 16, lineHeight: 1, padding: 2, flexShrink: 0 }}
            >
              ×
            </button>
          </div>
        );
      })}
    </div>
  );
}
